import React from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Row, Col, Card, CardBody, CardTitle } from "reactstrap";
import classNames from "classnames";
import { useSnapshot } from "valtio";

import { store } from "@vStore";

import { useResource } from "@hooks/useResource";

import {
    capitalize,
    getAdvancedPeriodStringParam,
    getTypeParam,
} from "@helpers/general";

import CustomErrorBoundary from "@components/Common/CustomErrorBoundary";
import ErrorPage from "@components/Common/ErrorPage";
import GroupedCustomSelect from "@components/Common/GroupedCustomSelect";

export default function SelectFilter() {
    const { t } = useTranslation();

    const { mainColor } = useSnapshot(store);
    const period = useSelector((state: any) => state.period);
    const type = useSelector((state: any) => state.type);
    const advancedDate = useSelector(
        (state: AdvancedDateStateType) => state.advancedDate
    );
    const selections = useSelector((state: any) => state.selections);

    const [regulationModes, setRegulationModes] = React.useState<string[]>([
        t("All regulation methods"),
    ]);
    const [amounts, setAmounts] = React.useState<number[]>([]);
    const [selectGroupOptions, setSelectGroupOptions] = React.useState<
        SelectFilterType[]
    >([
        {
            label: t("All regulation methods"),
            options: [t("All regulation methods")],
        },
    ]);

    const { resourceData: regulationModesData, error } = useResource(
        `${
            process.env.REACT_APP_API_V1_URL
        }/statistics/regulations/global?type=${getTypeParam(
            type
        )}&${getAdvancedPeriodStringParam(period, advancedDate)}${
            selections.designation && selections.designation !== "my_favorites"
                ? `&selected=${selections.designation}`
                : ""
        }`
    );

    React.useEffect(() => {
        let localRegulationModes: string[] = [];
        let localAmounts: number[] = [];
        let localDefaultRegulationModes: CardsModesType = {
            modes: [],
            amounts: [],
        };
        let localOptions: string[] = [];

        let array: SelectFilterType[] = [];

        regulationModesData.data?.forEach(
            (mode: {
                designation: string;
                value: number;
                data: { designation: string; value: number }[];
            }) => {
                if (mode.designation === "All regulation methods") {
                    localDefaultRegulationModes.modes.push(t(mode.designation));
                    localDefaultRegulationModes.amounts.push(mode.value);
                    localRegulationModes.push(mode.designation);
                    localAmounts.push(mode.value);
                } else {
                    if (mode.designation === "Physical Payment Method") {
                        localOptions = [];
                        mode?.data.forEach(
                            (element: {
                                designation: string;
                                value: number;
                            }) => {
                                localRegulationModes.push(element.designation);
                                localAmounts.push(element.value);
                                localOptions.push(
                                    t(capitalize(element.designation))
                                );
                            }
                        );

                        array.push({
                            label: mode.designation,
                            options: localOptions,
                        });
                    } else if (mode.designation === "Digital Payment Method") {
                        localOptions = [];
                        mode?.data.forEach(
                            (element: {
                                designation: string;
                                value: number;
                            }) => {
                                localRegulationModes.push(element.designation);
                                localAmounts.push(element.value);
                                localOptions.push(
                                    t(capitalize(element.designation))
                                );
                            }
                        );

                        array.push({
                            label: mode.designation,
                            options: localOptions,
                        });
                    }
                }
            }
        );

        setSelectGroupOptions([
            ...[
                {
                    label: t("All regulation methods"),
                    options: [t("All regulation methods")],
                },
            ],
            ...array,
        ]);

        setRegulationModes(
            localRegulationModes.map((el: string) => t(capitalize(el)))
        );
        setAmounts(localAmounts);
    }, [regulationModesData, t]);

    return (
        <React.Fragment>
            <Row>
                <Col>
                    <Card
                        className={classNames({
                            "dark-bg__clz": mainColor === null,
                        })}
                        style={{ background: mainColor }}
                    >
                        <CardBody className="py-1 px-3">
                            {error ? (
                                <ErrorPage />
                            ) : (
                                <CustomErrorBoundary>
                                    <Row className="d-flex align-items-center">
                                        <div
                                            style={{
                                                minWidth: "18%",
                                                paddingLeft: "12px",
                                            }}
                                        >
                                            <CardTitle
                                                tag="h4"
                                                className="text-white"
                                            >
                                                {t(
                                                    "Select your regulation modes"
                                                )}
                                            </CardTitle>
                                        </div>
                                        <Col
                                            xs="10"
                                            sm="6"
                                            md="5"
                                            lg="5"
                                            xl="4"
                                        >
                                            <GroupedCustomSelect
                                                options={selectGroupOptions}
                                                amounts={amounts}
                                                allModes={regulationModes}
                                            />
                                        </Col>
                                    </Row>
                                </CustomErrorBoundary>
                            )}
                        </CardBody>
                    </Card>
                </Col>
            </Row>
        </React.Fragment>
    );
}
